import type { MapStand } from "../shared/types";

export interface VenueMapBoothTextRun {
  text: string;
  x: number;
  y: number;
  size: number;
  weight: number;
  align: "center" | "left" | "right";
  /** Rotation about this run’s anchor, relative to the company-label transform. */
  rotation?: number;
}

export interface VenueMapBoothTextLayout {
  text: VenueMapBoothTextRun[];
  bounds: { minX: number; minY: number; maxX: number; maxY: number };
}

/** Admin quarter turns are relative to the lettering direction in the source. */
export function venueMapBoothLabelRotation(stand: MapStand) {
  return stand.label.rotation + (stand.labelRotation ?? 0);
}

type Point = readonly [number, number];
type Rectangle = { halfWidth: number; halfHeight: number };
type RoofRectangle = Rectangle & { x: number; y: number };
const EPSILON = 1e-8;

function pointOnSegment([x, y]: Point, [ax, ay]: Point, [bx, by]: Point) {
  const cross = (x - ax) * (by - ay) - (y - ay) * (bx - ax);
  return (
    Math.abs(cross) <= EPSILON * Math.max(1, Math.hypot(bx - ax, by - ay)) &&
    x >= Math.min(ax, bx) - EPSILON &&
    x <= Math.max(ax, bx) + EPSILON &&
    y >= Math.min(ay, by) - EPSILON &&
    y <= Math.max(ay, by) + EPSILON
  );
}

function pointInPolygon(point: Point, polygon: Point[]) {
  const [x, y] = point;
  let inside = false;
  for (
    let index = 0, previous = polygon.length - 1;
    index < polygon.length;
    previous = index++
  ) {
    const a = polygon[previous];
    const b = polygon[index];
    if (pointOnSegment(point, a, b)) return true;
    if (
      a[1] > y !== b[1] > y &&
      x < ((b[0] - a[0]) * (y - a[1])) / (b[1] - a[1]) + a[0]
    )
      inside = !inside;
  }
  return inside;
}

/** A boundary crossing the rectangle's interior exposes a concavity, even when all four corners are inside. */
function crossesInterior(a: Point, b: Point, rectangle: Rectangle) {
  const extents = [
    rectangle.halfWidth - EPSILON,
    rectangle.halfHeight - EPSILON,
  ];
  if (extents.some((extent) => extent <= 0)) return false;
  let start = 0;
  let end = 1;
  for (let axis = 0; axis < 2; axis++) {
    const delta = b[axis] - a[axis];
    if (Math.abs(delta) < EPSILON) {
      if (Math.abs(a[axis]) >= extents[axis]) return false;
      continue;
    }
    const intersections = [
      (-extents[axis] - a[axis]) / delta,
      (extents[axis] - a[axis]) / delta,
    ].sort((left, right) => left - right);
    start = Math.max(start, intersections[0]);
    end = Math.min(end, intersections[1]);
    if (start >= end) return false;
  }
  return start < end;
}

function containsRectangle(polygon: Point[], rectangle: Rectangle) {
  const { halfWidth: x, halfHeight: y } = rectangle;
  if (
    ![
      [-x, -y],
      [x, -y],
      [x, y],
      [-x, y],
    ].every((point) => pointInPolygon(point as [number, number], polygon))
  )
    return false;
  return polygon.every(
    (point, index) =>
      !crossesInterior(point, polygon[(index + 1) % polygon.length], rectangle),
  );
}

/** Interior candidates for a concave roof whose imported text anchor is in a gap. */
function interiorCentres(polygon: Point[]): Point[] {
  const levels = [...new Set(polygon.map(([, y]) => y))].sort((a, b) => a - b);
  const candidates: { point: Point; width: number }[] = [];
  // Imported outlines can have many vertices. A bounded scan keeps label assembly
  // predictable, and each candidate is still checked against the complete roof.
  const bands = levels.length - 1;
  const samples = Math.min(16, bands);
  for (let sample = 0; sample < samples; sample++) {
    const index = Math.floor((sample * bands) / samples);
    const y = (levels[index] + levels[index + 1]) / 2;
    const crossings: number[] = [];
    for (let index = 0; index < polygon.length; index++) {
      const a = polygon[index];
      const b = polygon[(index + 1) % polygon.length];
      if (a[1] > y !== b[1] > y)
        crossings.push(a[0] + ((y - a[1]) * (b[0] - a[0])) / (b[1] - a[1]));
    }
    crossings.sort((a, b) => a - b);
    for (let index = 0; index + 1 < crossings.length; index += 2)
      candidates.push({
        point: [(crossings[index] + crossings[index + 1]) / 2, y],
        width: crossings[index + 1] - crossings[index],
      });
  }
  return candidates
    .sort((a, b) => b.width - a.width)
    .slice(0, 16)
    .map(({ point }) => point);
}

function findUsableRoof(
  stand: MapStand,
  rotation = venueMapBoothLabelRotation(stand),
): RoofRectangle | undefined {
  const radians = (rotation * Math.PI) / 180;
  const cosine = Math.cos(radians);
  const sine = Math.sin(radians);
  // Prefer the component which owns a valid source anchor. If that anchor was
  // placed in an aisle/notch, fit the label inside a real component instead of
  // falling back to authored text floating outside the booth.
  const anchored = stand.polygons.some((polygon) =>
    pointInPolygon(
      [stand.label.x, stand.label.y],
      polygon.map(([x, y]): Point => [x, y]),
    ),
  );
  let best: RoofRectangle | undefined;
  let bestArea = 0;
  for (const source of stand.polygons) {
    if (source.length < 3) continue;
    // Inverse of the existing label transform: coordinates and rotation stay intact.
    const sourcePolygon: Point[] = source.map(([x, y]) => {
      const dx = x - stand.label.x;
      const dy = y - stand.label.y;
      return [dx * cosine + dy * sine, dy * cosine - dx * sine];
    });
    if (anchored && !pointInPolygon([0, 0], sourcePolygon)) continue;
    const minX = Math.min(...sourcePolygon.map(([x]) => x));
    const maxX = Math.max(...sourcePolygon.map(([x]) => x));
    const minY = Math.min(...sourcePolygon.map(([, y]) => y));
    const maxY = Math.max(...sourcePolygon.map(([, y]) => y));
    const roofCentreX = (minX + maxX) / 2;
    const roofCentreY = (minY + maxY) / 2;
    const fullRectangle = {
      halfWidth: (maxX - minX) / 2,
      halfHeight: (maxY - minY) / 2,
    };
    if (
      fullRectangle.halfWidth > EPSILON &&
      fullRectangle.halfHeight > EPSILON &&
      containsRectangle(
        sourcePolygon.map(([x, y]) => [x - roofCentreX, y - roofCentreY]),
        fullRectangle,
      )
    ) {
      // The bounding rectangle is maximal. Most imported booths take this
      // exact, inexpensive path instead of searching proportions and scales.
      const area = fullRectangle.halfWidth * fullRectangle.halfHeight;
      if (area > bestArea) {
        bestArea = area;
        best = {
          x: roofCentreX,
          y: roofCentreY,
          halfWidth: fullRectangle.halfWidth * 0.9,
          halfHeight: fullRectangle.halfHeight * 0.9,
        };
      }
      continue;
    }
    // Source text anchors need not be centred. Considering the roof centre lets
    // rectangular booths use their full roof without moving their stored anchor.
    const centres: Point[] = [
      [0, 0],
      [(minX + maxX) / 2, (minY + maxY) / 2],
    ];
    if (
      !centres.some(
        (point) =>
          point[0] > minX + EPSILON &&
          point[0] < maxX - EPSILON &&
          point[1] > minY + EPSILON &&
          point[1] < maxY - EPSILON &&
          pointInPolygon(point, sourcePolygon),
      )
    )
      centres.push(...interiorCentres(sourcePolygon));
    for (const [centreX, centreY] of centres) {
      const polygon: Point[] = sourcePolygon.map(([x, y]) => [
        x - centreX,
        y - centreY,
      ]);
      if (!pointInPolygon([0, 0], polygon)) continue;
      const halfWidth = Math.min(centreX - minX, maxX - centreX);
      const halfHeight = Math.min(centreY - minY, maxY - centreY);
      if (halfWidth <= EPSILON || halfHeight <= EPSILON) continue;
      // Several proportions avoid wasting an L-shaped booth on its narrowest notch.
      const ratios = [
        halfWidth / halfHeight,
        0.25,
        0.4,
        0.6,
        0.8,
        1,
        1.25,
        1.5,
        2,
        2.5,
        3,
        4,
      ];
      for (const ratio of ratios) {
        const width = Math.min(halfWidth, halfHeight * ratio);
        const height = width / ratio;
        let low = 0;
        let high = 1;
        for (let iteration = 0; iteration < 28; iteration++) {
          const scale = (low + high) / 2;
          if (
            containsRectangle(polygon, {
              halfWidth: width * scale,
              halfHeight: height * scale,
            })
          )
            low = scale;
          else high = scale;
        }
        const area = width * height * low * low;
        if (area > bestArea) {
          bestArea = area;
          // Five percent roof padding keeps glyphs and images off the wall.
          best = {
            x: centreX,
            y: centreY,
            halfWidth: width * low * 0.9,
            halfHeight: height * low * 0.9,
          };
        }
      }
    }
  }
  return best;
}

/** Conservative Arial widths without needing DOM/canvas measurement during scene assembly. */
export function venueMapBoothTextWidth(text: string, size: number) {
  let units = 0;
  for (const character of text) {
    if (/\s/.test(character)) units += 0.35;
    else if (/[MWmw@%&]/.test(character)) units += 1;
    else if (/[A-Z]/.test(character)) units += 0.8;
    else if (/[a-z0-9]/.test(character)) units += 0.68;
    else if (/['.,:;!|]/.test(character)) units += 0.4;
    else units += 1.15;
  }
  return Math.max(0.1, units) * size;
}

const roofs = new WeakMap<MapStand, RoofRectangle | undefined>();
const sourceRoofs = new WeakMap<MapStand, RoofRectangle | undefined>();

/** A padded label-aligned rectangle entirely inside the anchored roof polygon. */
export function venueMapBoothUsableRoof(stand: MapStand) {
  if (roofs.has(stand)) return roofs.get(stand);
  const roof = findUsableRoof(stand);
  roofs.set(stand, roof);
  return roof;
}

/** Share the same physical roof corner in all three label modes. */
export function venueMapBoothLabelFrame(stand: MapStand) {
  const roof = venueMapBoothUsableRoof(stand);
  if (!roof || roof.halfWidth <= EPSILON || roof.halfHeight <= EPSILON)
    return undefined;
  const width = roof.halfWidth * 2;
  const height = roof.halfHeight * 2;
  const showId = stand.label.showId !== false;
  const idSize = !showId
    ? 0
    : Math.min(
        stand.label.idFontSize,
        width / venueMapBoothTextWidth(stand.id, 1),
        height * 0.18,
      );
  const gap = Math.min(width, height) * 0.045;
  let id: VenueMapBoothTextRun = {
    text: stand.id,
    x: roof.x - roof.halfWidth,
    y: roof.y - roof.halfHeight + idSize,
    size: idSize,
    weight: 700,
    align: "left",
  };
  let body = {
    minX: roof.x - roof.halfWidth,
    maxX: roof.x + roof.halfWidth,
    minY: roof.y - roof.halfHeight + (showId ? idSize * 1.3 + gap : 0),
    maxY: roof.y + roof.halfHeight,
  };
  const rotation = venueMapBoothLabelRotation(stand);
  if (showId && Math.abs(rotation % 360) > EPSILON) {
    if (!sourceRoofs.has(stand))
      sourceRoofs.set(stand, findUsableRoof(stand, 0));
    const sourceRoof = sourceRoofs.get(stand);
    if (sourceRoof) {
      // The company may run vertically, but its number still belongs to the
      // physical top-left roof corner and reads with the map's other numbers.
      const size = Math.min(
        stand.label.idFontSize,
        (sourceRoof.halfWidth * 2) / venueMapBoothTextWidth(stand.id, 1),
        sourceRoof.halfHeight * 2 * 0.18,
      );
      const x = sourceRoof.x - sourceRoof.halfWidth;
      const y = sourceRoof.y - sourceRoof.halfHeight + size;
      const radians = (rotation * Math.PI) / 180;
      id = {
        ...id,
        x: x * Math.cos(radians) + y * Math.sin(radians),
        y: y * Math.cos(radians) - x * Math.sin(radians),
        size,
        rotation: -rotation,
      };
      // For a new quarter turn, only the number’s actual ink needs clearance.
      // Keep imported/default layouts unchanged until the admin opts in.
      if (stand.labelRotation) body.minY = roof.y - roof.halfHeight;
      const number = venueMapBoothTextBounds([id]);
      if (
        number.minX < body.maxX + gap &&
        number.maxX > body.minX - gap &&
        number.minY < body.maxY + gap &&
        number.maxY > body.minY - gap
      ) {
        // Retain the existing company region unless the independent number
        // crosses it. A single rectangular inset keeps logos undistorted too.
        const candidates = [
          { ...body, maxX: Math.min(body.maxX, number.minX - gap) },
          { ...body, minX: Math.max(body.minX, number.maxX + gap) },
          { ...body, maxY: Math.min(body.maxY, number.minY - gap) },
          { ...body, minY: Math.max(body.minY, number.maxY + gap) },
        ].filter((item) => item.maxX > item.minX && item.maxY > item.minY);
        candidates.sort(
          (a, b) =>
            (b.maxX - b.minX) * (b.maxY - b.minY) -
            (a.maxX - a.minX) * (a.maxY - a.minY),
        );
        body = candidates[0] ?? { ...body, maxX: body.minX, maxY: body.minY };
      }
    }
  }
  return {
    width: body.maxX - body.minX,
    height,
    gap,
    id,
    centreX: (body.minX + body.maxX) / 2,
    bodyTop: body.minY,
    bodyBottom: body.maxY,
  };
}

/** Authored placement is only used when a roof cannot accommodate fitted lettering. */
export function venueMapBoothFallbackNameLines(stand: MapStand) {
  const override = stand.mapLabel?.trim();
  return override
    ? [{ text: override, y: stand.label.nameLines[0]?.y ?? 4 }]
    : stand.label.nameLines;
}

/** Without a usable roof, preserve the authored number while turning the company text. */
export function venueMapBoothFallbackText(
  stand: MapStand,
): VenueMapBoothTextRun[] {
  const offset = stand.labelRotation ?? 0;
  const radians = (offset * Math.PI) / 180;
  return [
    ...(stand.label.showId === false
      ? []
      : [
          {
            text: stand.id,
            x: stand.label.idY * Math.sin(radians),
            y: stand.label.idY * Math.cos(radians),
            size: stand.label.idFontSize,
            weight: 700,
            align: "center" as const,
            ...(offset ? { rotation: -offset } : {}),
          },
        ]),
    ...venueMapBoothFallbackNameLines(stand).map((line) => ({
      text: line.text,
      x: 0,
      y: line.y,
      size: stand.label.nameFontSize,
      weight: 600,
      align: "center" as const,
    })),
  ];
}

function boothNameLines(stand: MapStand) {
  const override = stand.mapLabel?.trim();
  if (override) return [override];
  const lines = stand.label.nameLines
    .map((line) => line.text.trim())
    .filter((line) => line && line !== "." && line !== stand.id);
  // A quarter turn changes the available line length. Re-wrap the words rather
  // than preserving a stack of short lines from the original narrow direction.
  if (lines.length) return stand.labelRotation ? [lines.join(" ")] : lines;
  const name = (stand.shortName || stand.name).trim();
  return name && name !== "." && name !== stand.id ? [name] : [];
}

const graphemes =
  typeof Intl.Segmenter === "function"
    ? new Intl.Segmenter(undefined, { granularity: "grapheme" })
    : undefined;

function wrapNames(
  names: string[],
  width: number,
  size: number,
  breakLongWords = false,
) {
  const lines: string[] = [];
  for (const name of names) {
    let line = "";
    for (const word of name.split(/\s+/)) {
      if (venueMapBoothTextWidth(word, size) > width) {
        if (!breakLongWords) return undefined;
        if (line) lines.push(line);
        line = "";
        const segments = graphemes
          ? Array.from(graphemes.segment(word), ({ segment }) => segment)
          : Array.from(word);
        for (const segment of segments) {
          if (venueMapBoothTextWidth(segment, size) > width) return undefined;
          if (line && venueMapBoothTextWidth(line + segment, size) > width) {
            lines.push(line);
            line = segment;
          } else line += segment;
        }
        continue;
      }
      const next = line ? `${line} ${word}` : word;
      if (line && venueMapBoothTextWidth(next, size) > width) {
        lines.push(line);
        line = word;
      } else line = next;
    }
    if (line) lines.push(line);
  }
  return lines;
}

/** Find readable word wrapping before reducing type, preserving authored line breaks. */
export function venueMapBoothNames(
  stand: MapStand,
  width: number,
  height: number,
) {
  const names = boothNameLines(stand);
  if (!names.length || width <= 0 || height <= 0)
    return { lines: [] as string[], size: 0, height: 0 };
  // Imported lettering may already have been shrunk to fit a narrow booth.
  // An explicit quarter turn opts into a fresh fit, including larger type;
  // retaining the source font ceiling would only rotate that tiny lettering.
  const maximumSize = stand.labelRotation
    ? height / 1.3
    : Math.min(
        Math.max(stand.label.nameFontSize, stand.label.idFontSize * 1.2),
        height / 1.3,
      );
  const fit = (breakLongWords = false, parts = names) => {
    let low = 0;
    let high = maximumSize;
    let lines: string[] = [];
    const fullSize = wrapNames(parts, width, high, breakLongWords);
    if (fullSize && fullSize.length * high * 1.3 <= height)
      return {
        lines: fullSize,
        size: high,
        height: fullSize.length * high * 1.3,
      };
    // Font height and wrapped line count both grow monotonically with size.
    for (let iteration = 0; iteration < 24; iteration++) {
      const size = (low + high) / 2;
      const candidate = wrapNames(parts, width, size, breakLongWords);
      if (candidate && candidate.length * size * 1.3 <= height) {
        low = size;
        lines = candidate;
      } else high = size;
    }
    return { lines, size: low, height: lines.length * low * 1.3 };
  };
  const ordinary = fit();
  // Keep ordinary names intact. Only split an overlong token when whole-word
  // wrapping would make it tiny, and the available height gives a clear gain.
  if (
    names.length === 1 &&
    !/\s/.test(names[0]) &&
    ordinary.size < maximumSize * 0.55
  ) {
    const words = names[0]
      .replace(/([a-z0-9])([A-Z])/g, "$1\n$2")
      .replace(/([A-Z])([A-Z][a-z])/g, "$1\n$2")
      .split("\n");
    if (words.length > 1) {
      const preferred = fit(false, words);
      if (preferred.size > ordinary.size * 1.5) return preferred;
    }
    const broken = fit(true);
    if (broken.size > ordinary.size * 1.5) return broken;
  }
  return ordinary;
}

export function venueMapBoothTextBounds(text: VenueMapBoothTextRun[]) {
  const corners = text.flatMap((run) => {
    const width = venueMapBoothTextWidth(run.text, run.size);
    const left =
      run.align === "left" ? 0 : -width * (run.align === "right" ? 1 : 0.5);
    const right =
      run.align === "right" ? 0 : width * (run.align === "left" ? 1 : 0.5);
    const radians = ((run.rotation ?? 0) * Math.PI) / 180;
    return [
      [left, -run.size],
      [right, -run.size],
      [right, run.size * 0.3],
      [left, run.size * 0.3],
    ].map(([x, y]) => ({
      x: run.x + x * Math.cos(radians) - y * Math.sin(radians),
      y: run.y + x * Math.sin(radians) + y * Math.cos(radians),
    }));
  });
  return {
    minX: Math.min(...corners.map(({ x }) => x)),
    minY: Math.min(...corners.map(({ y }) => y)),
    maxX: Math.max(...corners.map(({ x }) => x)),
    maxY: Math.max(...corners.map(({ y }) => y)),
  };
}

const textLayouts = new WeakMap<
  MapStand,
  VenueMapBoothTextLayout | undefined
>();

/** Coordinates are relative to the existing label anchor, before its rotation. */
export function venueMapBoothTextLayout(
  stand: MapStand,
): VenueMapBoothTextLayout | undefined {
  if (textLayouts.has(stand)) return textLayouts.get(stand);
  const frame = venueMapBoothLabelFrame(stand);
  if (!frame) {
    textLayouts.set(stand, undefined);
    return undefined;
  }
  const names = venueMapBoothNames(
    stand,
    frame.width,
    frame.bodyBottom - frame.bodyTop,
  );
  const top = (frame.bodyTop + frame.bodyBottom - names.height) / 2;
  const text: VenueMapBoothTextRun[] = [
    ...(stand.label.showId === false ? [] : [frame.id]),
    ...names.lines.map((name, index): VenueMapBoothTextRun => ({
      text: name,
      x: frame.centreX,
      y: top + names.size + index * names.size * 1.3,
      size: names.size,
      weight: 600,
      align: "center",
    })),
  ];
  const layout = { text, bounds: venueMapBoothTextBounds(text) };
  textLayouts.set(stand, layout);
  return layout;
}
